import type { SearchAndDiscoveryPageProps } from '@eventbrite/datalayer-library';
import { moment } from '@eventbrite/date';
import reduce from 'lodash/reduce';
import {
    CUSTOM_DATE,
    EVENTBRITE_CATEGORY_TO_REFINEMENT,
    EVENTBRITE_FORMAT_TO_REFINEMENT,
    EVENTBRITE_SUBCATEGORY_TO_REFINEMENT,
} from '../../constants';
import {
    FILTER_TYPE_GA_MAP,
    GA_SEARCH_CATEGORY,
} from '../../constants/analytics';
import type { EventSearch } from '../../types/Search';
import { trackEventClickInternal } from '../api/analytics';
/* Internal Tracking */
export const trackInternalAppClick = (trackingData: object) => {
    const defaultData = {
        platform: 'web',
        timestamp: parseInt(moment().format('X'), 10),
        selectionType: 'event',
    };

    trackEventClickInternal({ ...defaultData, ...trackingData });
};

/*
 * @param selectedFilter -- object -- ${filterType}: ${filterValue}
 *      - Filter object passed to search API upon selection
 * @param customDate -- object -- {startDate: 'string', endDate: 'string'}
 *      - Optional param that exists when filterValue === CUSTOM_DATE
 * @param category -- string
 *      - Future proof support for if filters need to be associated to category other than search
 *
 * Function takes in filter selection information and creates the necessary anayltics tracking
 * body from it for GA tracking
 */
export const getFilterEventTrackingData = (
    selectedFilter?: object,
    customDate?: { startDate?: string; endDate?: string },
    category: string = GA_SEARCH_CATEGORY,
) =>
    reduce(
        selectedFilter,
        (memo: any, filterValue: string, filterType: string) => {
            if (filterValue || filterValue === '') {
                if (filterType in FILTER_TYPE_GA_MAP) {
                    let label = filterValue;
                    const filterAnalyticsAction =
                        FILTER_TYPE_GA_MAP[filterType];

                    //Special case for customDate
                    if (filterValue === CUSTOM_DATE) {
                        const { startDate, endDate } = customDate || {};

                        label = `${startDate}--${endDate}`;
                    }

                    //Special case for category
                    if (EVENTBRITE_CATEGORY_TO_REFINEMENT[filterValue]) {
                        label = EVENTBRITE_CATEGORY_TO_REFINEMENT[filterValue];
                    }

                    //Special case for format
                    if (EVENTBRITE_FORMAT_TO_REFINEMENT[filterValue]) {
                        label = EVENTBRITE_FORMAT_TO_REFINEMENT[filterValue];
                    }

                    return [
                        ...memo,
                        {
                            category,
                            label,
                            action: filterAnalyticsAction,
                        },
                    ];
                }
            }

            return memo;
        },
        [],
    );

function joinOrDefault(array: string[], defaultValue: string): string {
    return array.length ? array.join(', ') : defaultValue;
}

function transformValue(
    key: string,
    value: any,
): string | number | boolean | {} {
    let transformedValue = value;
    switch (key) {
        case 'dateRange': {
            transformedValue = value.to ? value : 'any';
            break;
        }
        case 'dates': {
            if (Array.isArray(value) && value.length === 1)
                transformedValue = value[0];
            else if (Array.isArray(value) && value.length > 1)
                transformedValue = value
                    .filter((value) => value !== 'current_future')
                    .join(',');
            else transformedValue = 'any';
            break;
        }
        case 'price': {
            transformedValue = value ? value : 'any';
            break;
        }
        case 'tags': {
            transformedValue = joinOrDefault(value, '');
            break;
        }
        case 'currencies': {
            transformedValue = joinOrDefault(value, 'any');
            break;
        }
        case 'languages': {
            transformedValue = joinOrDefault(value, 'any');
            break;
        }
        case 'special': {
            transformedValue = value.length ? true : false;
            break;
        }
        case 'tagsCategoryId': {
            transformedValue = value.length
                ? EVENTBRITE_CATEGORY_TO_REFINEMENT[value[0]]
                : 'any';
            break;
        }
        case 'tagsSubCategoryId': {
            transformedValue = value.length
                ? EVENTBRITE_SUBCATEGORY_TO_REFINEMENT[
                      value[0] as keyof typeof EVENTBRITE_SUBCATEGORY_TO_REFINEMENT
                  ]
                : 'any';
            break;
        }
        case 'tagsFormatId': {
            transformedValue = value.length
                ? EVENTBRITE_FORMAT_TO_REFINEMENT[value[0]]
                : 'any';
            break;
        }
        // Start Default Fallthrough Cases that do not require transformation.
        // Listed for completeness to reflect consideration for the property.
        case 'topMatchShown':
        case 'dedup':
        case 'includePromotedEvents':
        case 'onlineEventsOnly':
        case 'page':
        case 'pageSize':
        case 'places':
        case 'q':
        default: {
            transformedValue = value;
            break;
        }
    }

    return transformedValue;
}

/**
 * This Lookup map transforms known keys to values we want to preferred names in heap.
 * These names have been chosen to reduce ambiguity and likelyhood of collision with event props.
 * These names end up as column names in snowflake for events.
 */
const searchKeyTransformations: Record<string, string> = {
    currencies: 'search_filter_currencies',
    dateRange: 'search_filter_date_range',
    dates: 'search_filter_dates_relative_to',
    dedup: 'deDupedResultsShown',
    includePromotedEventsFor: 'search_filter_include_promoted_events',
    languages: 'search_filter_langugages',
    onlineEventsOnly: 'search_filter_online_events_only',
    page: 'search_filter_page_number',
    pageSize: 'search_filter_page_size',
    price: 'search_filter_price',
    q: 'search_bar_keywords',
    special: 'search_filter_following_organizers',
    tagsCategoryId: 'search_filter_category_id',
    tagsSubCategoryId: 'search_filter_subcategory_id',
    tagsFormatId: 'search_filter_format_id',
    tags: 'search_filter_tags',
    places: 'search_filter_location',
};

function tagHasKey(key: string) {
    return (candidate: string) => candidate.indexOf(key) > -1;
}

function reshapeFilterTags(
    tags: string[],
    location: { name: string; placeId: string },
): [string, any][] {
    const hasCategory = tagHasKey('EventbriteCategory');
    const hasSubCategory = tagHasKey('EventbriteSubCategory');
    const hasFormat = tagHasKey('EventbriteFormat');

    const tagsCategoryId = tags.filter(hasCategory);
    const tagsSubCategoryId = tags.filter(hasSubCategory);
    const tagsFormatId = tags.filter(hasFormat);
    const otherTags = tags.filter(
        (x: string) => !hasCategory(x) && !hasSubCategory(x) && !hasFormat(x),
    );

    return [
        ['tags', otherTags],
        ['tagsCategoryId', tagsCategoryId],
        ['tagsSubCategoryId', tagsSubCategoryId],
        ['tagsFormatId', tagsFormatId],
        ['places', location],
    ];
}

export function transformSearchAndDiscovery(
    input: EventSearch,
    location: { name: string; placeId: string },
): SearchAndDiscoveryPageProps {
    const entries: [string, any][] = [
        ...Object.entries(input).filter(
            // These keys are overriden below.
            ([key, _]) => key != 'tags' && key != 'places',
        ),
        ...reshapeFilterTags(input.tags || [], location),
    ];

    const transformed: [string, any][] = entries.map(
        ([key, value]: [string, any]): [string, any] => {
            const newKey = searchKeyTransformations[key] || key;
            return [newKey, transformValue(key, value)];
        },
    );

    return Object.fromEntries(transformed) as SearchAndDiscoveryPageProps;
}
